/**
 * Guarded access to window.localStorage.
 *
 * Safari (private browsing, "Block all cookies", strict tracking prevention) and
 * some embedded webviews throw a SecurityError the moment `window.localStorage`
 * is *accessed* — not just written to. Any unguarded access therefore crashes the
 * React tree and shows the full-page error screen.
 *
 * `installStorageFallback()` replaces the throwing property with an in-memory
 * store so third-party code (including the generated Supabase client) keeps
 * working, and the helpers below never throw.
 */

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem" | "clear" | "key" | "length">;

function createMemoryStorage(): StorageLike {
  const map = new Map<string, string>();
  return {
    get length() {
      return map.size;
    },
    getItem: (key: string) => (map.has(key) ? map.get(key)! : null),
    setItem: (key: string, value: string) => {
      map.set(key, String(value));
    },
    removeItem: (key: string) => {
      map.delete(key);
    },
    clear: () => {
      map.clear();
    },
    key: (index: number) => Array.from(map.keys())[index] ?? null,
  };
}

let fallbackInstalled = false;

/** True when the browser refused real storage and we're using a memory shim. */
export let usingMemoryStorage = false;

function storageWorks(): boolean {
  try {
    const store = window.localStorage;
    if (!store) return false;
    const probe = "__mp_probe__";
    store.setItem(probe, "1");
    store.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

/**
 * Call once, as early as possible on the client. Safe to call repeatedly.
 */
export function installStorageFallback(): void {
  if (typeof window === "undefined" || fallbackInstalled) return;
  fallbackInstalled = true;

  if (storageWorks()) return;

  usingMemoryStorage = true;
  const memory = createMemoryStorage();
  try {
    Object.defineProperty(window, "localStorage", {
      configurable: true,
      get: () => memory,
    });
  } catch {
    // Nothing else we can do — the helpers below still degrade gracefully.
  }
  try {
    if (!sessionStorageWorks()) {
      const memorySession = createMemoryStorage();
      Object.defineProperty(window, "sessionStorage", {
        configurable: true,
        get: () => memorySession,
      });
    }
  } catch {
    /* ignore */
  }
}

function sessionStorageWorks(): boolean {
  try {
    const store = window.sessionStorage;
    if (!store) return false;
    const probe = "__mp_probe__";
    store.setItem(probe, "1");
    store.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

function store(): StorageLike | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage ?? null;
  } catch {
    installStorageFallback();
    try {
      return window.localStorage ?? null;
    } catch {
      return null;
    }
  }
}

export function safeGetItem(key: string): string | null {
  try {
    return store()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function safeSetItem(key: string, value: string): void {
  try {
    store()?.setItem(key, value);
  } catch {
    /* storage unavailable or full — preferences simply don't persist */
  }
}

export function safeRemoveItem(key: string): void {
  try {
    store()?.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Read a JSON value, returning null when missing or unparseable. */
export function safeGetJson<T>(key: string): T | null {
  const raw = safeGetItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function safeSetJson(key: string, value: unknown): void {
  try {
    safeSetItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}
